import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import FilterSection from '../components/FilterSection';
import FilterChips from '../components/FilterChips';
import AggregatedDashboard from '../components/AggregatedDashboard';
import Tooltip from '../components/UI/Tooltip';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import { sanitizeText } from '../utils/sanitize';

// Shared utility function for engine icons
const getEngineIcon = (engine) => {
  const engineName = engine.toLowerCase();
  const logoMap = {
    'openai': '/logos/chatgpt.png',
    'chatgpt': '/logos/chatgpt.png',
    'gemini': '/logos/gemini.png',
    'perplexity': '/logos/perplexity.png'
  };
  
  const logoSrc = logoMap[engineName];
  if (logoSrc) {
    return (
      <img 
        src={logoSrc} 
        alt={`${engine} logo`}
        className="w-8 h-8 rounded-full object-cover"
      />
    );
  }
  return <div className="w-8 h-8 rounded-full bg-gray-300"></div>;
};

// Rankings View Component
const RankingsView = ({ batchId, clientFilter, setClientFilter, hideZeroCompetitors, setHideZeroCompetitors, sortBy, setSortBy, viewMode }) => {
  const { isDark } = useTheme();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create per-engine analyses from individual database records
  const createPerEngineAnalysesFromDB = async (batchId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/search-execution/batches/${batchId}/individual-records`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch individual records');
      }

      const { clientRecords, competitorRecords } = await response.json();
      
      // Group records by query and engine to create analyses
      const analysisMap = new Map();

      // Process client records
      clientRecords.forEach(client => {
        const key = `${client.query}_${client.engine}`;
        if (!analysisMap.has(key)) {
          analysisMap.set(key, {
            query: sanitizeText(client.query),
            engine: client.engine,
            clientName: sanitizeText(client.client_name),
            clientFound: client.occurrences > 0 || client.first_position !== null,
            clientRank: 1,
            totalEntities: 0,
            competitorsBefore: [],
            competitorsAfter: [],
            allPositions: [],
            answerLength: client.answer_length,
            clientPosition: client.first_position || 'Not Found',
            isClientNotFound: !client.first_position
          });
        }

        const analysis = analysisMap.get(key);
        if (client.first_position) {
          analysis.allPositions.push({
            name: sanitizeText(client.client_name),
            type: 'client',
            position: client.first_position,
            positionPercentage: ((client.first_position / client.answer_length) * 100),
            mentions: client.occurrences,
            score: 0
          });
        }
      });

      // Process competitor records
      competitorRecords.forEach(competitor => {
        const key = `${competitor.query}_${competitor.engine}`;
        if (!analysisMap.has(key)) {
          return;
        }

        const analysis = analysisMap.get(key);
        if (competitor.first_position) {
          analysis.allPositions.push({
            name: sanitizeText(competitor.competitor_name),
            type: 'competitor',
            position: competitor.first_position,
            positionPercentage: ((competitor.first_position / competitor.answer_length) * 100),
            mentions: competitor.occurrences,
            score: 0
          });
        }
      });

      // Calculate rankings for each analysis
      const analyses = Array.from(analysisMap.values()).map(analysis => {
        analysis.allPositions.sort((a, b) => a.position - b.position);
        
        const clientIndex = analysis.allPositions.findIndex(e => e.type === 'client');
        const competitorCount = analysis.allPositions.filter(e => e.type === 'competitor').length;
        
        if (clientIndex !== -1) {
          // Client found - normal ranking
          analysis.clientRank = clientIndex + 1;
          analysis.totalEntities = analysis.allPositions.length;
          analysis.isClientNotFound = false;
          analysis.competitorsBefore = analysis.allPositions.slice(0, clientIndex).filter(e => e.type === 'competitor');
          analysis.competitorsAfter = analysis.allPositions.slice(clientIndex + 1).filter(e => e.type === 'competitor');
        } else {
          // Client not found - special handling
          analysis.clientRank = null; // Will be displayed as "Not Found"
          analysis.totalEntities = competitorCount; // Only count competitors
          analysis.isClientNotFound = true;
          analysis.competitorsBefore = analysis.allPositions.filter(e => e.type === 'competitor');
          analysis.competitorsAfter = [];
        }

        return analysis;
      });

      console.log('Database-based analyses created:', analyses.length);
      analyses.forEach(analysis => {
        console.log(`${analysis.query} (${analysis.engine}): Client rank ${analysis.clientRank}/${analysis.totalEntities}, ${analysis.competitorsBefore.length} before + ${analysis.competitorsAfter.length} after`);
      });

      return analyses;

    } catch (error) {
      console.error('Failed to create analyses from database:', error);
      throw error;
    }
  };

  useEffect(() => {
    const loadAnalyses = async () => {
      try {
        setLoading(true);
        const dbAnalyses = await createPerEngineAnalysesFromDB(batchId);
        setAnalyses(dbAnalyses);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (batchId) {
      loadAnalyses();
    }
  }, [batchId]);

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-8 text-center animate-fade-in-scale">
        <div className="glass-spinner w-8 h-8 mx-auto mb-4"></div>
        <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading ranking data from database...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card rounded-xl p-8 text-center animate-fade-in-scale">
        <p className="text-red-400 mb-4 text-mono text-sm">Error loading rankings: {error}</p>
        <p className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>This might be because the execution hasn't been run with the new system yet.</p>
      </div>
    );
  }

  // Apply filters
  let filteredAnalyses = analyses;
  
  // Client visibility filter
  if (clientFilter === 'found') {
    filteredAnalyses = filteredAnalyses.filter(analysis => analysis.clientFound);
  } else if (clientFilter === 'notfound') {
    filteredAnalyses = filteredAnalyses.filter(analysis => !analysis.clientFound);
  }
  
  // Competitors filter
  if (hideZeroCompetitors) {
    filteredAnalyses = filteredAnalyses.filter(
      analysis => analysis.competitorsBefore.length > 0 || analysis.competitorsAfter.length > 0
    );
  }
  
  // Apply sorting
  let sortedAnalyses = [...filteredAnalyses];
  if (sortBy === 'position') {
    sortedAnalyses.sort((a, b) => {
      if (a.isClientNotFound) return 1;
      if (b.isClientNotFound) return -1;
      return a.clientPosition - b.clientPosition;
    });
  } else if (sortBy === 'rank') {
    sortedAnalyses.sort((a, b) => a.clientRank - b.clientRank);
  } else if (sortBy === 'engine') {
    sortedAnalyses.sort((a, b) => a.engine.localeCompare(b.engine));
  }

  const clearAllFilters = () => {
    setClientFilter('all');
    setHideZeroCompetitors(false);
    setSortBy('position');
  };

  return (
    <div className={`${viewMode === 'simple' ? 'p-10 space-y-12' : 'p-16 space-y-20'}`}>
      {/* Page Header */}
      <div className={`glass-card rounded-xl ${viewMode === 'simple' ? 'p-8' : 'p-12'} ${viewMode === 'simple' ? 'mb-8' : 'mb-16'}`}>
        <div className="flex items-center justify-between">
          <h2 className={`heading-mono text-xl font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>Rankings Analysis</h2>
          <div className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Database-based individual records
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <FilterSection
        clientFilter={clientFilter}
        setClientFilter={setClientFilter}
        hideZeroCompetitors={hideZeroCompetitors}
        setHideZeroCompetitors={setHideZeroCompetitors}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filteredCount={sortedAnalyses.length}
        totalCount={analyses.length}
        onClearFilters={clearAllFilters}
        hideSearch={true}
        viewMode={viewMode}
      />

      {/* Active Filter Chips */}
      <FilterChips
        clientFilter={clientFilter}
        setClientFilter={setClientFilter}
        hideZeroCompetitors={hideZeroCompetitors}
        setHideZeroCompetitors={setHideZeroCompetitors}
        sortBy={sortBy}
        setSortBy={setSortBy}
        onClearAll={clearAllFilters}
        hideSearch={true}
      />
      
      {/* Enhanced Statistics Panel */}
      <div className={`glass-card rounded-xl ${viewMode === 'simple' ? 'p-8' : 'p-12'}`}>
        <div className={`flex items-center gap-2 ${viewMode === 'simple' ? 'mb-6' : 'mb-8'}`}>
          <svg className={`w-5 h-5 ${isDark ? 'text-accent-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className={`heading-mono text-lg font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>Analysis Overview</h3>
        </div>
        <div className={`grid grid-cols-2 md:grid-cols-4 ${viewMode === 'simple' ? 'gap-6' : 'gap-8'}`}>
          <div className={`glass-dark rounded-lg ${viewMode === 'simple' ? 'p-6' : 'p-8'}`}>
            <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{analyses.length}</div>
            <div className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>Total combinations</div>
          </div>
          <div className={`glass-dark rounded-lg ${viewMode === 'simple' ? 'p-6' : 'p-8'}`}>
            <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>
              {analyses.filter(a => a.competitorsBefore.length > 0 || a.competitorsAfter.length > 0).length}
            </div>
            <div className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>With competitors</div>
          </div>
          <div className={`glass-dark rounded-lg ${viewMode === 'simple' ? 'p-6' : 'p-8'}`}>
            <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{sortedAnalyses.length}</div>
            <div className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>Filtered results</div>
          </div>
          <div className={`glass-dark rounded-lg ${viewMode === 'simple' ? 'p-6' : 'p-8'}`}>
            <div className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Data Source</div>
            <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Individual database records</div>
          </div>
        </div>
      </div>
      
      {/* Results */}
      {sortedAnalyses.length === 0 ? (
        <div className="glass-alert-warning rounded-xl p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-yellow-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className={`heading-mono text-lg font-medium mb-2 ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>No results match your filters</h3>
          <p className={`text-mono text-sm mb-4 ${isDark ? 'text-yellow-200' : 'text-yellow-600'}`}>
            Try adjusting the filters above or clearing all filters to see all {analyses.length} available results.
          </p>
          <button
            onClick={clearAllFilters}
            className="btn-glass-accent px-4 py-2 rounded-lg text-mono font-medium hover:scale-105 transform transition-all"
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <div className={viewMode === 'simple' ? 'space-y-8' : 'space-y-20'}>
          {sortedAnalyses.map((analysis, index) => (
            viewMode === 'simple' ? (
              // Simple Rankings - Just position numbers
              <div key={`${analysis.query}-${analysis.engine}`} className="glass-card rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {getEngineIcon(analysis.engine)}
                  <span className={`text-mono font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{analysis.query}</span>
                </div>
                <div className={`text-2xl font-bold ${analysis.isClientNotFound ? 'text-red-400' : isDark ? 'text-accent-400' : 'text-blue-600'}`}>
                  {analysis.isClientNotFound ? 'N/F' : `#${analysis.clientRank}`}
                </div>
              </div>
            ) : (
              // Detailed Rankings - Full implementation
              <div key={`${analysis.query}-${analysis.engine}`} className="glass-card rounded-xl p-6 overflow-hidden">
              {/* Engine Logo and Rank in Top Left */}
              <div className="flex items-start gap-4 mb-4">
                <div className="flex flex-col items-start gap-4">
                  {/* Engine Logo */}
                  <div className="flex items-center mb-4">
                    {getEngineIcon(analysis.engine)}
                  </div>
                  
                  {/* Rank Display */}
                  <div className="flex items-baseline gap-1">
                    {analysis.isClientNotFound ? (
                      <span className="text-2xl font-bold text-red-400">Not Found</span>
                    ) : (
                      <>
                        <span className={`text-3xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>#{analysis.clientRank}</span>
                        <span className={`text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>of {analysis.totalEntities}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Query Text - 8 Words Per Line */}
              <div className="mb-4">
                <h3 className={`heading-mono text-2xl font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`} style={{lineHeight: '1.6'}}>
                  {analysis.query.split(' ').reduce((lines, word, index) => {
                    const lineIndex = Math.floor(index / 8);
                    if (!lines[lineIndex]) lines[lineIndex] = [];
                    lines[lineIndex].push(word);
                    return lines;
                  }, []).map((line, lineIndex) => (
                    <div key={lineIndex}>{line.join(' ')}</div>
                  ))}
                </h3>
              </div>

              {/* Position Bar */}
              {!analysis.isClientNotFound && analysis.allPositions.length > 0 && (
                <div className="mb-6">
                  <div className={`text-mono text-sm mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>Position in Answer</div>
                  <div className="relative h-8 glass-dark rounded-full overflow-hidden">
                    {analysis.allPositions.map((entity, idx) => {
                      const left = Math.max(0, Math.min(98, (entity.position / analysis.answerLength) * 100));
                      return (
                        <div
                          key={idx}
                          className={`absolute h-full w-2 rounded-sm ${
                            entity.type === 'client' ? (isDark ? 'bg-accent-400' : 'bg-blue-600') + ' z-10' : 'bg-gray-500 z-5'
                          }`}
                          style={{ 
                            left: `${left}%`,
                            transform: 'translateX(-50%)'
                          }}
                          title={`${entity.name} at position ${entity.position}`}
                        />
                      );
                    })}
                  </div>
                  <div className={`flex justify-between text-mono text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span>Start (0%)</span>
                    <span>End (100%)</span>
                  </div>
                </div>
              )}

              {/* Two-Column Layout: Ranking Order + Competitor Boxes */}
              {viewMode === 'detailed' && (
                <div className="grid grid-cols-2 gap-6 mt-6">
                  {/* Left Column: Complete Ranking Order */}
                  <div className="glass-dark p-4 rounded-lg">
                    <div className={`text-mono text-sm font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>Complete Ranking Order</div>
                    <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                      {analysis.allPositions.map((entity, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 text-mono text-sm p-2 rounded ${
                            entity.type === 'client' ? 'glass-accent font-semibold' : 'bg-white/5'
                          }`}
                        >
                          <span className={`w-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>#{idx + 1}</span>
                          <span className={`flex-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>{entity.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Column: Competitor Boxes */}
                  <div className="space-y-4">
                    {/* Competitors Before */}
                    <div className="glass-dark p-4 rounded-lg">
                      <div className={`text-mono text-sm mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>Competitors Before</div>
                      <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'} mb-2`}>{analysis.competitorsBefore.length}</div>
                      {analysis.competitorsBefore.length > 0 && (
                        <div className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {analysis.competitorsBefore.map(c => c.name).join(', ')}
                        </div>
                      )}
                    </div>

                    {/* Competitors After */}
                    <div className="glass-dark p-4 rounded-lg">
                      <div className={`text-mono text-sm mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>Competitors After</div>
                      <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'} mb-2`}>{analysis.competitorsAfter.length}</div>
                      {analysis.competitorsAfter.length > 0 && (
                        <div className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {analysis.competitorsAfter.map(c => c.name).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Simple Mode Competitors */}
              {viewMode === 'simple' && (
                <div className="mb-4">
                  {(analysis.competitorsBefore.length > 0 || analysis.competitorsAfter.length > 0) && (
                    <div className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span>Competitors: </span>
                      <span className={isDark ? 'text-white' : 'text-gray-800'}>
                        {[...analysis.competitorsBefore, ...analysis.competitorsAfter].map(c => c.name).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

const ResultsDashboard = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedExecutions, setExpandedExecutions] = useState(new Set());
  const [expandedResponses, setExpandedResponses] = useState(new Set());
  const [selectedView, setSelectedView] = useState('dashboard'); // 'dashboard', 'list', 'detail', 'rankings', 'sources'
  const [searchSources, setSearchSources] = useState([]);
  const [viewMode, setViewMode] = useState(() => {
    // Load saved preference or default to 'detailed'
    return localStorage.getItem('resultsViewMode') || 'detailed';
  });
  
  
  // Rankings view filters and sorting (like original rankflow)
  const [hideZeroCompetitors, setHideZeroCompetitors] = useState(false);
  const [sortBy, setSortBy] = useState('position'); // 'default', 'position', 'engine'
  const [clientFilter, setClientFilter] = useState('all'); // 'all', 'found', 'notfound'

  useEffect(() => {
    if (batchId && user) {
      loadResults();
    }
  }, [batchId, user]); // eslint-disable-line react-hooks/exhaustive-deps


  const loadResults = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      
      // Load batch status, execution results, and search sources
      const [batchResponse, executionsResponse, sourcesResponse] = await Promise.all([
        fetch(`${apiUrl}/search-execution/batches/${batchId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }),
        fetch(`${apiUrl}/search-execution/batches/${batchId}/results`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }),
        fetch(`${apiUrl}/search-execution/batches/${batchId}/individual-records`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        })
      ]);

      if (batchResponse.ok && executionsResponse.ok) {
        const batchData = await batchResponse.json();
        const executionsData = await executionsResponse.json();
        
        setResults({
          batch: batchData.batch,
          executions: executionsData.executions || []
        });
        
        // Also load search sources if available
        if (sourcesResponse.ok) {
          const sourcesData = await sourcesResponse.json();
          setSearchSources(sourcesData.searchSources || []);
        }
      } else {
        throw new Error('Failed to load results');
      }
    } catch (error) {
      console.error('Failed to load results:', error);
      setError('Failed to load execution results');
    } finally {
      setLoading(false);
    }
  };

  const toggleExecutionExpansion = (executionId) => {
    const newExpanded = new Set(expandedExecutions);
    if (newExpanded.has(executionId)) {
      newExpanded.delete(executionId);
    } else {
      newExpanded.add(executionId);
    }
    setExpandedExecutions(newExpanded);
  };

  const toggleResponse = (responseId) => {
    const newExpanded = new Set(expandedResponses);
    if (newExpanded.has(responseId)) {
      newExpanded.delete(responseId);
    } else {
      newExpanded.add(responseId);
    }
    setExpandedResponses(newExpanded);
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'detailed' ? 'simple' : 'detailed';
    setViewMode(newMode);
    localStorage.setItem('resultsViewMode', newMode);
  };





  const formatExecutionTime = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };



  const exportResults = (format) => {
    // Basic CSV export functionality
    if (format === 'csv' && results?.executions) {
      const headers = ['Search Term', 'Status', 'Client Score', 'Client Mentions', 'Competitors Found', 'Execution Time', 'OpenAI Status', 'Gemini Status', 'Perplexity Status'];
      const rows = results.executions.map(execution => [
        execution.searchTerm,
        execution.status,
        execution.ai_analysis_results?.summary?.overallClientScore || 0,
        execution.ai_analysis_results?.summary?.clientMentions || 0,
        execution.ai_analysis_results?.summary?.topCompetitors?.length || 0,
        execution.executionTime ? formatExecutionTime(execution.executionTime) : 'N/A',
        execution.results?.engines?.openai?.status || 'N/A',
        execution.results?.engines?.gemini?.status || 'N/A',
        execution.results?.engines?.perplexity?.status || 'N/A'
      ]);
      
      const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `search-results-${results.batch.name.replace(/[^a-zA-Z0-9]/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pt-20">
        <div className="glass-card rounded-2xl p-8 text-center animate-fade-in-scale">
          <h1 className={`heading-mono text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Sign In Required</h1>
          <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Please sign in to view search results.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pt-20">
        <div className="glass-card rounded-2xl p-8 text-center animate-fade-in-scale">
          <div className="flex items-center justify-center">
            <div className="glass-spinner w-8 h-8 mr-3"></div>
            <span className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading results...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pt-20">
        <div className="glass-card rounded-2xl p-8 text-center animate-fade-in-scale">
          <div className="glass-dark rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className={`heading-mono text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>Error Loading Results</h1>
          <p className={`text-mono text-sm mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{error}</p>
          <button
            onClick={() => navigate('/search')}
            className="btn-glass-accent px-6 py-3 rounded-lg text-mono font-medium hover:scale-105 transform transition-all"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pt-20">
        <div className="glass-card rounded-2xl p-8 text-center animate-fade-in-scale">
          <div className="glass-dark rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className={`heading-mono text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Results Not Found</h1>
          <p className={`text-mono text-sm mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>The requested batch results could not be found.</p>
          <button
            onClick={() => navigate('/search')}
            className="btn-glass-accent px-6 py-3 rounded-lg text-mono font-medium hover:scale-105 transform transition-all"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 pt-20 scroll-container">
      {/* Combined Header and Execution Summary */}
      <div className={`glass-card rounded-2xl ${viewMode === 'simple' ? 'p-8' : 'p-10'} ${viewMode === 'simple' ? 'mb-6' : 'mb-8'}`}>
        {/* Top Row - Back button and Export */}
        <div className={`flex items-center justify-between ${viewMode === 'simple' ? 'mb-6' : 'mb-8'}`}>
          <button
            onClick={() => navigate('/search')}
            className="btn-glass flex items-center gap-2 px-4 py-2 rounded-lg hover:scale-105 transform transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Search
          </button>
          <button
            onClick={() => exportResults('csv')}
            className="btn-glass-accent px-4 py-2 rounded-lg text-mono text-sm flex items-center gap-2 hover:scale-105 transform transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>

        {/* Middle Row - Title and View Mode Toggle */}
        <div className={`flex items-start justify-between ${viewMode === 'simple' ? 'mb-6' : 'mb-8'}`}>
          <div>
            <h1 className={`heading-mono text-2xl font-bold mb-2 text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>
              AI Search Results Dashboard
            </h1>
            <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{results.batch.name}</p>
          </div>
          <button
            onClick={toggleViewMode}
            className="glass-tab px-4 py-2 rounded-lg text-mono text-sm flex items-center gap-2 transition-all bg-accent-400/20 border-accent-400 hover:bg-accent-400/30"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            {viewMode === 'detailed' ? 'Detailed View' : 'Simple View'}
          </button>
        </div>

        {/* Execution Summary - System Message Style */}
        <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} text-center mt-6 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {results.batch.completedQueries || 0} completed, {results.batch.failedQueries || 0} failed of {results.batch.totalQueries || 0} total
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`glass-card rounded-xl p-0 overflow-hidden ${viewMode === 'simple' ? 'mb-8' : 'mb-10'}`}>
        <div className="flex items-stretch">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            )},
            { id: 'list', label: 'List', icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            )},
            { id: 'rankings', label: 'Rankings', icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            )},
            { id: 'sources', label: 'Sources', icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            )}
          ].map((tab, index, array) => (
            <button
              key={tab.id}
              onClick={() => setSelectedView(tab.id)}
              className={`flex-1 px-6 py-4 text-mono text-sm font-medium flex items-center justify-center gap-3 transition-all relative ${
                selectedView === tab.id 
                  ? isDark 
                    ? 'bg-accent-400/10 text-accent-400 border-b-2 border-accent-400' 
                    : 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                  : isDark 
                    ? 'hover:bg-white/5 text-gray-400 hover:text-white border-b-2 border-transparent' 
                    : 'hover:bg-gray-50 text-gray-600 hover:text-gray-800 border-b-2 border-transparent'
              } ${index < array.length - 1 ? 'border-r border-white/10' : ''}`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
              {/* Mobile: Show only icons, Desktop: Show icons + labels */}
              <span className="sm:hidden">{tab.label.substring(0, 1)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Results Content */}
      {results.executions.length === 0 ? (
        <div className="glass-card rounded-xl animate-fade-in-scale">
          <div className="text-center py-12 text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className={`heading-mono text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>No Results Available</h3>
            <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>No execution results are available for this batch.</p>
          </div>
        </div>
      ) : selectedView === 'dashboard' ? (
          // Dashboard View - Aggregated Dashboard
          <div className="animate-fade-in-scale">
            <AggregatedDashboard executions={results.executions} searchSources={searchSources} viewMode={viewMode} />
          </div>
        ) : selectedView === 'list' ? (
          // Table View
          viewMode === 'simple' ? (
            // Simple Table View - Just essentials
            <div className="glass-card rounded-xl animate-fade-in-scale">
              <div className="overflow-x-auto p-8">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className={`px-6 py-4 text-left text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Search Term</th>
                      <th className={`px-6 py-4 text-center text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Score</th>
                      <th className={`px-6 py-4 text-center text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.executions.map((execution) => (
                      <tr key={execution.id} className="border-b border-white/5">
                        <td className="px-6 py-6">
                          <div className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate max-w-xs`}>
                            {execution.searchTerm}
                          </div>
                        </td>
                        <td className="px-6 py-6 text-center">
                          <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>
                            {execution.ai_analysis_results?.summary?.overallClientScore || 0}
                          </div>
                        </td>
                        <td className="px-6 py-6 text-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            execution.status === 'completed' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {execution.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            // Detailed Table View - Current implementation with expandable rows
            <div className="glass-card rounded-xl animate-fade-in-scale">
              <div className={`overflow-x-auto p-12`}>
              <table className="min-w-full divide-y divide-white/10">
                <thead className="glass-dark">
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Search Term</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      <Tooltip content="Score based on mentions (20pts each, max 80) + position bonus (20pts max for early appearance) + sentiment adjustment (+3 positive/-5 negative words)">
                        <div className="flex items-center gap-1">
                          Client Score
                          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </Tooltip>
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Competitors</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Actions</th>
                  </tr>
                </thead>
              <tbody className="glass-card divide-y divide-white/10">
                {results.executions.map((execution) => (
                  <React.Fragment key={execution.id}>
                    <tr 
                      className={`${viewMode === 'detailed' ? 'glass-interactive hover:bg-white/5' : ''} transition-all cursor-pointer`}
                      onClick={() => toggleExecutionExpansion(execution.id)}
                    >
                    <td className={`px-6 ${viewMode === 'simple' ? 'py-6' : 'py-8'} whitespace-nowrap`}>
                      <div className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'} truncate max-w-xs`} title={execution.searchTerm}>
                        {execution.searchTerm}
                      </div>
                    </td>
                    <td className={`px-6 ${viewMode === 'simple' ? 'py-6' : 'py-8'} whitespace-nowrap text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {viewMode === 'detailed' ? (
                        <span title="Score: Mentions (20pts each, max 80) + Position bonus (20pts max) + Sentiment (+3/-5pts)">
                          {execution.ai_analysis_results?.summary?.overallClientScore || 0}/100
                        </span>
                      ) : (
                        <span>{execution.ai_analysis_results?.summary?.overallClientScore || 0}</span>
                      )}
                    </td>
                    <td className={`px-6 ${viewMode === 'simple' ? 'py-6' : 'py-8'} whitespace-nowrap text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {execution.ai_analysis_results?.summary?.topCompetitors?.length || 0}
                    </td>
                    <td className={`px-6 ${viewMode === 'simple' ? 'py-6' : 'py-4'} whitespace-nowrap text-sm font-medium`}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExecutionExpansion(execution.id);
                        }}
                        className="btn-glass text-mono text-sm px-3 py-1 rounded-lg hover:scale-105 transform transition-all"
                      >
                        {expandedExecutions.has(execution.id) ? 'Hide Details' : 'View Details'}
                      </button>
                    </td>
                  </tr>
                  {expandedExecutions.has(execution.id) && (
                    <tr>
                      <td colSpan={viewMode === 'simple' ? "4" : "4"} className="px-6 py-4 glass-dark">
                        <div className="space-y-4">
                          {/* AI Engine Results */}
                          {execution.results && typeof execution.results === 'object' && (
                            <div>
                              <h5 className={`text-mono font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>AI Engine Results</h5>
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {Object.entries(execution.results.engines || {}).map(([engine, result]) => (
                                  <div key={engine} className="glass-card p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-3">
                                      {getEngineIcon(engine)}
                                      <span className={`px-2 py-1 rounded-full text-xs text-white ${
                                        result.status === 'success' ? 'bg-green-500' : 'bg-red-500'
                                      }`}>
                                        {result.status}
                                      </span>
                                    </div>
                                    {result.status === 'success' ? (
                                      <div className="space-y-2">
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <div className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Answer Preview</div>
                                            <button
                                              onClick={() => toggleResponse(`${execution.id}-${engine}`)}
                                              className="text-mono text-xs text-accent-400 hover:text-accent-300"
                                            >
                                              {expandedResponses.has(`${execution.id}-${engine}`) ? 'Hide Full Answer' : 'Show Full Answer'}
                                            </button>
                                          </div>
                                          {expandedResponses.has(`${execution.id}-${engine}`) ? (
                                            <div className={`text-mono text-sm max-h-96 overflow-y-auto glass-dark rounded p-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                              <div className="whitespace-pre-wrap">{result.textAnswer}</div>
                                            </div>
                                          ) : (
                                            <div className={`text-mono text-sm max-h-12 overflow-hidden ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                              {result.textAnswer?.substring(0, 200)}...
                                            </div>
                                          )}
                                        </div>
                                        <div>
                                          <div className={`text-mono text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Citations</div>
                                          <div className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                            {result.citations?.length || 0} sources
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-mono text-sm text-red-400">
                                        {result.error || 'Search failed'}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Competitor Analysis */}
                          {execution.ai_analysis_results && typeof execution.ai_analysis_results === 'object' && (
                            <div>
                              <h5 className={`text-mono font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>Competitor Analysis</h5>
                              <div className="glass-card p-4 rounded-lg">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                  <div>
                                    <div className="text-sm text-gray-600 mb-1">
                                      <span title="Score calculation: Mentions (20pts each, max 80) + Position bonus (20pts max for early appearance) + Sentiment adjustment (+3 positive/-5 negative context words)">
                                        Client Score ℹ️
                                      </span>
                                    </div>
                                    <div className="text-lg font-semibold text-blue-600">
                                      {execution.ai_analysis_results.summary?.overallClientScore || 0}/100
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-600 mb-1">Client Mentions</div>
                                    <div className="text-lg font-semibold text-green-600">
                                      {execution.ai_analysis_results.summary?.clientMentions || 0}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-600 mb-1">Competitors Found</div>
                                    <div className="text-lg font-semibold text-orange-600">
                                      {execution.ai_analysis_results.summary?.topCompetitors?.length || 0}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-600 mb-1">Engines Analyzed</div>
                                    <div className="text-lg font-semibold text-gray-900">
                                      {execution.ai_analysis_results.summary?.enginesAnalyzed || 0}/{execution.ai_analysis_results.summary?.totalEngines || 0}
                                    </div>
                                  </div>
                                </div>

                                {viewMode === 'detailed' && execution.ai_analysis_results.summary?.topCompetitors && 
                                 execution.ai_analysis_results.summary.topCompetitors.length > 0 && (
                                  <div>
                                    <div className="text-sm text-gray-600 mb-2">Top Competitors</div>
                                    <div className="space-y-2">
                                      {execution.ai_analysis_results.summary.topCompetitors.slice(0, 3).map((competitor, idx) => (
                                        <div key={idx} className="flex items-center justify-between bg-white p-2 rounded">
                                          <span className="text-sm font-medium">{competitor.name}</span>
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500">Score: {competitor.averageScore}</span>
                                            <span className="text-xs text-gray-400">
                                              {competitor.mentions} mention{competitor.mentions !== 1 ? 's' : ''}
                                              {competitor.engineCount && ` in ${competitor.engineCount} engine${competitor.engineCount !== 1 ? 's' : ''}`}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {viewMode === 'simple' && execution.ai_analysis_results.summary?.topCompetitors && 
                                 execution.ai_analysis_results.summary.topCompetitors.length > 0 && (
                                  <div>
                                    <div className="text-sm text-gray-600">Top 3 Competitors:</div>
                                    <div className="text-sm text-gray-900 mt-1">
                                      {execution.ai_analysis_results.summary.topCompetitors.slice(0, 3).map(c => c.name).join(', ')}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          )
        ) : selectedView === 'rankings' ? (
          // Rankings View - Individual query/engine analysis (like original rankflow)
          <div className="animate-fade-in-scale">
            <RankingsView batchId={batchId} 
                         clientFilter={clientFilter} 
                         setClientFilter={setClientFilter}
                         hideZeroCompetitors={hideZeroCompetitors} 
                         setHideZeroCompetitors={setHideZeroCompetitors}
                         sortBy={sortBy} 
                         setSortBy={setSortBy}
                         viewMode={viewMode} />
          </div>
        ) : selectedView === 'sources' ? (
          // Sources View - Display all citations/sources
          <div className="glass-card rounded-xl animate-fade-in-scale">
            <div className={`${viewMode === 'simple' ? 'p-12' : 'p-16'}`}>
            <div className={viewMode === 'simple' ? 'mb-10' : 'mb-12'}>
              <h3 className={`heading-mono text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                Sources & Citations Analysis
              </h3>
              {viewMode === 'detailed' && (
                <p className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Review all sources referenced by AI engines across your search queries
                </p>
              )}
            </div>

            {/* Summary Stats and Category Distribution */}
            <div className={`grid grid-cols-1 md:grid-cols-3 ${viewMode === 'simple' ? 'gap-6 mb-12' : 'gap-8 mb-16'}`}>
              {/* Metrics Column */}
              <div className="md:col-span-1 space-y-4">
                <div className="glass-card rounded-lg p-4">
                  <div className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Sources</div>
                  <div className={`text-2xl font-bold text-accent-400 mt-1`}>{searchSources.length}</div>
                </div>
                <div className="glass-card rounded-lg p-4">
                  <div className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Unique Domains</div>
                  <div className={`text-2xl font-bold text-purple-400 mt-1`}>
                    {new Set(searchSources.map(s => s.domain).filter(Boolean)).size}
                  </div>
                </div>
              </div>
              
              {/* Source Category Distribution Chart */}
              <div className="glass-card rounded-xl p-6 md:col-span-2">
                <h4 className={`heading-mono text-md mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  Source Categories Distribution
                </h4>
                <div className="h-80">
                {(() => {
                  // Calculate category distribution
                  const categoryCount = searchSources.reduce((acc, source) => {
                    const category = source.category || 'Unknown';
                    acc[category] = (acc[category] || 0) + 1;
                    return acc;
                  }, {});
                  
                  // Convert to array format for Recharts
                  const data = Object.entries(categoryCount)
                    .map(([name, value]) => ({ name, value }))
                    .sort((a, b) => b.value - a.value);
                  
                  // Define colors for each category
                  const COLORS = {
                    'Competitor': '#8b5cf6',
                    'Ranking': '#10b981', 
                    'Comparison': '#f59e0b',
                    'UserReviews': '#ef4444',
                    'BlogPost': '#3b82f6',
                    'News': '#06b6d4',
                    'Announcements': '#ec4899',
                    'Podcasts': '#84cc16',
                    'SocialMedia': '#f97316',
                    'Wikipedia': '#8b5cf6',
                    'Reddit': '#ff4500',
                    'Unknown': '#6b7280'
                  };
                  
                  const totalSources = searchSources.length;
                  
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[entry.name] || COLORS.Unknown} />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          contentStyle={{
                            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            padding: '8px',
                          }}
                          labelStyle={{ color: isDark ? '#fff' : '#000' }}
                          formatter={(value) => [`${value} sources`, 'Count']}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          wrapperStyle={{
                            paddingTop: '20px',
                            fontSize: '12px',
                            fontFamily: 'monospace'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  );
                })()}
                </div>
              </div>
            </div>
            
            {/* Sources by Domain */}
            <div className="glass-card rounded-xl p-6 mb-6">
              <h4 className={`heading-mono text-md mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                Top Referenced Domains
              </h4>
              <div className="space-y-3">
                {Object.entries(
                  searchSources.reduce((acc, source) => {
                    if (source.domain) {
                      acc[source.domain] = (acc[source.domain] || 0) + 1;
                    }
                    return acc;
                  }, {})
                )
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 10)
                  .map(([domain, count]) => (
                    <div key={domain} className="flex items-center justify-between glass-dark rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <svg className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        <span className={`text-mono font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{sanitizeText(domain)}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {count} citation{count > 1 ? 's' : ''}
                        </span>
                        <span className={`text-mono text-xs px-2 py-1 rounded-full ${
                          count > 5 ? 'bg-green-500/20 text-green-400' :
                          count > 2 ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {count > 5 ? 'High' : count > 2 ? 'Medium' : 'Low'} Impact
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* All Sources List - Only show in detailed mode */}
            {viewMode === 'detailed' && (
              <div className="glass-card rounded-xl p-6">
              <h4 className={`heading-mono text-md mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                All Sources by Query
              </h4>
              <div className="space-y-6">
                {results.executions
                  .filter(exec => searchSources.some(s => s.execution_id === exec.id))
                  .map(execution => {
                    const execSources = searchSources.filter(s => s.execution_id === execution.id);
                    const sourcesByEngine = execSources.reduce((acc, source) => {
                      if (!acc[source.engine]) acc[source.engine] = [];
                      acc[source.engine].push(source);
                      return acc;
                    }, {});

                    return (
                      <div key={execution.id} className="glass-dark rounded-lg p-4">
                        <div className="mb-3">
                          <div className={`text-mono font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                            {execution.searchTerm}
                          </div>
                          <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {execSources.length} sources across {Object.keys(sourcesByEngine).length} engines
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          {Object.entries(sourcesByEngine).map(([engine, sources]) => (
                            <div key={engine} className="ml-4">
                              <div className="flex items-center gap-2 mb-2">
                                {getEngineIcon(engine)}
                                <span className={`text-mono text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {engine} ({sources.length} sources)
                                </span>
                              </div>
                              <div className="ml-8 space-y-1">
                                {sources.map((source, idx) => (
                                  <div key={idx} className="flex items-center justify-between">
                                    <a 
                                      href={source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`text-mono text-xs hover:underline flex-1 truncate ${
                                        isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'
                                      }`}
                                    >
                                      {sanitizeText(source.title || source.domain || source.url)}
                                    </a>
                                    {source.source_type && (
                                      <span className={`text-mono text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                                        {source.source_type}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            )}
            </div>
          </div>
        ) : null}
    </div>
  );
};

export default ResultsDashboard;